// client/src/components/UnifiedSearchBar.tsx
//
// Self-contained search + filter preset component.
//
// Manages internally: basic search term, advanced query, search mode,
// advanced panel open/close, built-in preset selection, user preset
// CRUD (localStorage), and the save/overwrite/delete UI.
//
// The parent page only needs to:
//   1. Pass domain config (fieldConfigs, builtInPresets, storage keys)
//   2. Listen to `onSearchChange` to receive the effective search state
//   3. Pass `actions` for toolbar slots (DensityToggle, Add button, etc.)
//
// AdvancedSearch.tsx is never modified.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Pencil,
  SlidersHorizontal,
  Trash2,
} from 'lucide-react';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import BasicSearch from './TableSearch/BasicSearch';
import UnifiedAdvancedSearch from './UnifiedAdvancedSearch';
import type { AdvancedSearchQuery, FieldConfig } from './TableSearch/types';
import { createEmptyAdvancedQuery } from './TableSearch/types';
import type { FilterPreset } from '../shared/hooks/useFilterPresets';
import { readSessionStorage } from '../shared/hooks/useSessionStorageState';
import { GRAY_300, GRAY_50, GRAY_500, GRAY_700 } from 'theme/grayScale';
import { COLOR_DANGER, COLOR_INFO, COLOR_INFO_TEXT } from 'theme/colors';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/**
 * The effective search state emitted by `UnifiedSearchBar` whenever the user's
 * search intent changes.  The parent page uses this to build API query params.
 *
 * - `mode === 'basic'`    → use `basicTerm` for a simple text search
 * - `mode === 'advanced'` → use `advancedQuery` for structured filter rules
 */
export interface UnifiedSearchState {
  mode: 'basic' | 'advanced';
  basicTerm: string;
  advancedQuery: AdvancedSearchQuery;
}

/** Internal user-saved preset shape (not exported; callers use `FilterPreset`). */
interface UserPreset {
  name: string;
  query: AdvancedSearchQuery;
}

export interface UnifiedSearchBarProps {
  // ── Domain config ────────────────────────────────────────────────────────
  /** Field definitions for the advanced search panel. */
  fieldConfigs: FieldConfig[];
  /** Placeholder text for the basic search input. */
  placeholder?: string;

  // ── Preset system ────────────────────────────────────────────────────────
  /**
   * Read-only presets supplied by the domain (shown with 🔒 in the dropdown).
   * Omit to hide the preset dropdown entirely.
   */
  builtInPresets?: FilterPreset[];
  /**
   * localStorage key for persisting the user's saved preset library
   * (permanent — survives across browser sessions/restarts).
   * Required when `builtInPresets` is provided.
   */
  storageKeyUserPresets?: string;
  /**
   * sessionStorage key for persisting the *currently applied* live filter
   * state (mode, basic search term, advanced query, active preset name).
   * Session-scoped: resets to defaults at the start of each new browser
   * tab/window, but survives page refresh and in-app navigation within that
   * tab. Distinct from `storageKeyUserPresets`, which holds the permanent
   * saved-preset library.
   * Required when `builtInPresets` is provided.
   */
  storageKeyActivePreset?: string;

  /**
   * The preset to apply when the user presses Clear or × (defaults to empty
   * query / basic mode if omitted).
   * Pass the domain's "show all" preset here (e.g. `THERAPEUTIC_AREA_PRESETS[0]`).
   */
  defaultPreset?: FilterPreset;

  /**
   * Overrides the resolved initial state on mount — highest priority,
   * takes precedence over both the persisted session state and
   * `defaultPreset`. Resolved once on mount only (mirrors
   * `resolveInitialFilterState`'s own mount-once contract); later prop
   * changes are not reactive. For deep-link flows where a parent page
   * must seed the filter with a specific query before the user has
   * interacted with search at all (e.g. "View Products" from a
   * manufacturer row).
   */
  initialSearchState?: UnifiedSearchState;

  // ── Callbacks ────────────────────────────────────────────────────────────
  /** Called whenever the effective search state changes. */
  onSearchChange: (state: UnifiedSearchState) => void;

  // ── Toolbar slot ─────────────────────────────────────────────────────────
  /**
   * Extra content rendered to the right of the filter preset button
   * (e.g. `<DensityToggle />`, Add button, Upload button).
   */
  actions?: React.ReactNode;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function readLocalStorage<T>(key: string | undefined, fallback: T): T {
  if (!key) return fallback;
  try {
    const raw = localStorage.getItem(key);
    if (raw !== null) return JSON.parse(raw) as T;
  } catch {
    // ignore
  }
  return fallback;
}

function writeLocalStorage(key: string | undefined, value: unknown): void {
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore
  }
}

/** Session-scoped "currently applied" live filter state — see storageKeyActivePreset. */
interface StoredFilterSessionState {
  mode: 'basic' | 'advanced';
  basicTerm: string;
  advancedQuery: AdvancedSearchQuery;
  activePresetName: string;
}

function readFilterSessionState(
  key: string | undefined
): StoredFilterSessionState | null {
  if (!key) return null;
  return readSessionStorage<StoredFilterSessionState | null>(key, null);
}

/** Seeds fresh-session state from `defaultPreset` (e.g. domain's "All" preset). */
function seedFromDefaultPreset(
  defaultPreset: FilterPreset | undefined
): StoredFilterSessionState {
  const query = defaultPreset?.query ?? createEmptyAdvancedQuery();
  const hasRules = query.filterGroups.some(g => g.rules.length > 0);
  return {
    mode: hasRules ? 'advanced' : 'basic',
    basicTerm: '',
    advancedQuery: query,
    activePresetName: defaultPreset?.id ?? '',
  };
}

/**
 * Resolves the state to hydrate on mount: the persisted session state if one
 * exists (survives refresh/nav within the tab), otherwise `defaultPreset`
 * (e.g. "All Products") rather than a blank/"Custom" state.
 */
function resolveInitialFilterState(
  key: string | undefined,
  defaultPreset: FilterPreset | undefined
): StoredFilterSessionState {
  return readFilterSessionState(key) ?? seedFromDefaultPreset(defaultPreset);
}

function writeFilterSessionState(
  key: string | undefined,
  state: StoredFilterSessionState
): void {
  if (!key) return;
  try {
    sessionStorage.setItem(key, JSON.stringify(state));
  } catch {
    // ignore
  }
}

/**
 * Compare two queries for semantic equality, ignoring the random `id` fields
 * on FilterGroup and SearchRule (which are UUIDs and always differ).
 *
 * Two queries with zero total rules are always equal regardless of their
 * `filterGroups` wrapper shape — a built-in "All" preset is typically
 * `filterGroups: []`, but `UnifiedAdvancedSearch` normalizes an empty query to
 * `filterGroups: [emptyGroup]` once the panel is opened/submitted. Without
 * this fast path, submitting the panel unchanged on an "All" preset would
 * fail this comparison and incorrectly fall back to "Custom".
 */
function queriesAreEqual(
  a: AdvancedSearchQuery,
  b: AdvancedSearchQuery
): boolean {
  const totalRules = (q: AdvancedSearchQuery) =>
    q.filterGroups.reduce((sum, g) => sum + g.rules.length, 0);
  if (totalRules(a) === 0 && totalRules(b) === 0) return true;

  const normalize = (q: AdvancedSearchQuery) =>
    JSON.stringify({
      groupOperator: q.groupOperator,
      filterGroups: q.filterGroups.map(g => ({
        logicalOperator: g.logicalOperator,
        rules: g.rules.map(r => ({
          field: r.field,
          operator: r.operator,
          value: r.value,
        })),
      })),
    });
  return normalize(a) === normalize(b);
}

// ---------------------------------------------------------------------------
// Operator display labels and filter summary builder (used by collapsed state)
// ---------------------------------------------------------------------------

const OPERATOR_LABELS: Record<string, string> = {
  equals: '=',
  notEquals: '≠',
  contains: 'contains',
  greaterThan: '>',
  lessThan: '<',
  isBefore: 'before',
  isAfter: 'after',
  isBetween: 'between',
  isEmpty: 'is empty',
  isNotEmpty: 'is not empty',
};

/**
 * Builds a short human-readable summary of the active filter state for the
 * collapsed panel row (Option 4 — smart hybrid).
 * Dynamically matches `query` against known presets via `queriesAreEqual` so
 * the summary always reflects the live in-form state, not the last-searched
 * state stored in `activePresetName`.
 */
function buildSummaryText(
  fieldConfigs: FieldConfig[],
  query: AdvancedSearchQuery,
  builtInPresets: FilterPreset[] | undefined,
  userPresets: { name: string; query: AdvancedSearchQuery }[]
): string {
  // Match the current live query against known presets
  const matchBuiltIn = builtInPresets?.find(p =>
    queriesAreEqual(p.query, query)
  );
  if (matchBuiltIn) return `🔒 ${matchBuiltIn.label}`;
  const matchUser = userPresets.find(p => queriesAreEqual(p.query, query));
  if (matchUser) return matchUser.name;
  const rules = query.filterGroups.flatMap(g => g.rules);
  if (rules.length === 0) return 'No filters active';
  const MAX_SHOWN = 2;
  const shown = rules.slice(0, MAX_SHOWN);
  const overflow = rules.length - MAX_SHOWN;
  const parts = shown.map(rule => {
    const fieldLabel =
      fieldConfigs.find(f => f.key === rule.field)?.label ?? rule.field;
    const opLabel = OPERATOR_LABELS[rule.operator] ?? rule.operator;
    if (rule.operator === 'isEmpty' || rule.operator === 'isNotEmpty') {
      return `${fieldLabel} ${opLabel}`;
    }
    const rawVal = rule.value;
    const val = Array.isArray(rawVal)
      ? (rawVal as unknown as unknown[])
          .slice(0, 2)
          .map(v => (v instanceof Date ? v.toLocaleDateString() : String(v)))
          .join(', ') + ((rawVal as unknown[]).length > 2 ? '…' : '')
      : (rule.label ?? String(rawVal));
    return `${fieldLabel} ${opLabel} ${val}`;
  });
  return parts.join(' · ') + (overflow > 0 ? ` +${overflow} more` : '');
}

// ---------------------------------------------------------------------------
// UnifiedSearchBar
// ---------------------------------------------------------------------------

/**
 * Unified search bar component — combines basic search, advanced filter panel,
 * and filter preset system into a single self-contained unit.
 *
 * Drop in on any page that needs search + preset functionality.
 * Does NOT modify `AdvancedSearch.tsx`.
 */
export const UnifiedSearchBar: React.FC<UnifiedSearchBarProps> = ({
  fieldConfigs,
  placeholder = 'Search…',
  builtInPresets,
  storageKeyUserPresets,
  storageKeyActivePreset,
  onSearchChange,
  actions,
  defaultPreset,
  initialSearchState,
}) => {
  const hasPresets = Boolean(builtInPresets && builtInPresets.length > 0);

  // Resolved once on mount: `initialSearchState` (deep-link override) takes
  // precedence over the persisted session state, which itself takes
  // precedence over `defaultPreset` (e.g. "All Products") rather than a
  // blank/"Custom" state. Computed a single time and reused below — calling
  // resolveInitialFilterState() separately per useState initializer would
  // re-read sessionStorage and build independent (structurally-equal but
  // not reference-equal) query objects for each piece of state.
  const [initialFilterState] = useState(() =>
    initialSearchState
      ? {
          mode: initialSearchState.mode,
          basicTerm: initialSearchState.basicTerm,
          advancedQuery: initialSearchState.advancedQuery,
          activePresetName: '',
        }
      : resolveInitialFilterState(storageKeyActivePreset, defaultPreset)
  );

  // ── Search state (what the API sees) ────────────────────────────────────
  // Seeded from sessionStorage on mount so the "currently applied" filter
  // survives refresh + in-app navigation, but resets each new tab/window.
  const [basicTerm, setBasicTerm] = useState(initialFilterState.basicTerm);
  const [advancedQuery, setAdvancedQuery] = useState<AdvancedSearchQuery>(
    initialFilterState.advancedQuery
  );
  const [searchMode, setSearchMode] = useState<'basic' | 'advanced'>(
    initialFilterState.mode
  );

  // ── Panel UI state ───────────────────────────────────────────────────────
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  // An active advanced filter with rules starts in the collapsed-summary
  // state -- the same visible-chip / expand / × pattern any active filter
  // uses elsewhere in the app -- so the user has a clear indication a filter
  // is applied, rather than it being invisible behind "Filter options".
  //
  // Derived from `initialFilterState` (the fully-resolved state), not the
  // raw `initialSearchState` prop directly -- the prop is only present on
  // the mount that actually receives a deep link (an ordinary first-time
  // mount with no deep link never has it). `initialFilterState` folds in
  // the sessionStorage fallback for that ordinary case, so this always
  // reflects whatever filter is really active on mount, deep-linked or not.
  const [isCollapsed, setIsCollapsed] = useState(
    () =>
      initialFilterState.mode === 'advanced' &&
      initialFilterState.advancedQuery.filterGroups.some(
        g => g.rules.length > 0
      )
  );
  // Incremented to force AdvancedSearch to remount when a preset is applied
  const [panelResetKey, setPanelResetKey] = useState(0);

  // ── Preset state ─────────────────────────────────────────────────────────
  const [userPresets, setUserPresets] = useState<UserPreset[]>(() =>
    readLocalStorage<UserPreset[]>(storageKeyUserPresets, [])
  );
  const [activePresetName, setActivePresetName] = useState<string>(
    initialFilterState.activePresetName
  );
  // Tracks which user preset the panel was opened to edit. Set when a user
  // preset is applied; cleared on clear / built-in selection.
  const [sourcePresetName, setSourcePresetName] = useState<string | undefined>(
    undefined
  );
  // Name of the preset currently pending inline delete confirmation
  const [deletingPresetName, setDeletingPresetName] = useState<string | null>(
    null
  );
  // Name of the preset currently being renamed inline in the dropdown
  const [renamingPresetName, setRenamingPresetName] = useState<string | null>(
    null
  );
  const [renameInputValue, setRenameInputValue] = useState('');
  const [renameError, setRenameError] = useState('');

  // ── Filter dropdown anchor ───────────────────────────────────────────────
  const [filterMenuAnchorEl, setFilterMenuAnchorEl] =
    useState<HTMLButtonElement | null>(null);

  // ── Save-as-preset form state ────────────────────────────────────────────
  const [isSaveFormOpen, setIsSaveFormOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveError, setSaveError] = useState('');

  // ── Live query ───────────────────────────────────────────────────────────
  // `liveQueryRef` is used in save handlers (no render cost).
  // `liveQuery` state mirrors it and drives reactive derived values (button
  // enable/disable, duplicate detection) without needing useEffect.
  const liveQueryRef = useRef<AdvancedSearchQuery>(advancedQuery);
  const [liveQuery, setLiveQuery] = useState<AdvancedSearchQuery>(
    initialFilterState.advancedQuery
  );

  // ── Internal helpers ──────────────────────────────────────────────────────

  const emit = useCallback(
    (
      nextMode: 'basic' | 'advanced',
      nextBasicTerm: string,
      nextAdvancedQuery: AdvancedSearchQuery
    ) => {
      setSearchMode(nextMode);
      onSearchChange({
        mode: nextMode,
        basicTerm: nextBasicTerm,
        advancedQuery: nextAdvancedQuery,
      });
    },
    [onSearchChange]
  );

  // Persist the currently-applied live filter state to sessionStorage
  // whenever it changes (session-scoped — see storageKeyActivePreset).
  useEffect(() => {
    writeFilterSessionState(storageKeyActivePreset, {
      mode: searchMode,
      basicTerm,
      advancedQuery,
      activePresetName,
    });
  }, [
    storageKeyActivePreset,
    searchMode,
    basicTerm,
    advancedQuery,
    activePresetName,
  ]);

  // On mount, sync the resolved initial state (restored session state, or
  // `defaultPreset` when none exists yet) to the parent page — which
  // otherwise starts from its own blank default apiParams.
  useEffect(() => {
    onSearchChange({
      mode: initialFilterState.mode,
      basicTerm: initialFilterState.basicTerm,
      advancedQuery: initialFilterState.advancedQuery,
    });
    // One-time cleanup: this key used to hold the active preset name in
    // localStorage (permanent). It now lives in sessionStorage above — drop
    // the stale localStorage entry so it doesn't linger indefinitely.
    if (storageKeyActivePreset) {
      try {
        localStorage.removeItem(storageKeyActivePreset);
      } catch {
        // ignore
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount only, to sync restored session state to the parent
  }, []);

  // ── Basic search handlers ─────────────────────────────────────────────────

  const handleBasicSearchChange = useCallback(
    (term: string) => {
      setBasicTerm(term);
      emit('basic', term, createEmptyAdvancedQuery());
    },
    [emit]
  );

  const handleBasicSearchClear = useCallback(() => {
    setBasicTerm('');
    emit('basic', '', createEmptyAdvancedQuery());
  }, [emit]);

  // ── Advanced panel handlers ───────────────────────────────────────────────

  const handleAdvancedSearch = useCallback(
    (query: AdvancedSearchQuery) => {
      liveQueryRef.current = query;
      setLiveQuery(query);
      setAdvancedQuery(query);

      // Resolve the active preset label: if the submitted query is identical
      // to a saved preset, show that preset's name instead of "Custom".
      const matchBuiltIn = builtInPresets?.find(p =>
        queriesAreEqual(p.query, query)
      );
      const matchUser = userPresets.find(p => queriesAreEqual(p.query, query));

      if (matchBuiltIn) {
        setActivePresetName(matchBuiltIn.id);
        setSourcePresetName(undefined); // built-ins can't be updated
      } else if (matchUser) {
        setActivePresetName(matchUser.name);
        setSourcePresetName(matchUser.name); // enter Mode B for the matched preset
      } else {
        setActivePresetName('Custom');
        // Keep sourcePresetName as-is: if the user was editing a user preset,
        // stay in Mode B so "Update" remains available.
      }

      emit('advanced', '', query);
    },
    [emit, builtInPresets, userPresets]
  );

  // ── Revert to default preset and return to basic mode ───────────────────
  // Shared by × (both open and collapsed states) and the Clear button inside
  // the advanced form.  If `defaultPreset` is supplied it is re-applied;
  // otherwise the query resets to empty and basic search is restored.
  const handleRevertAndClose = useCallback(() => {
    const targetQuery = defaultPreset?.query ?? createEmptyAdvancedQuery();
    const targetPresetName = defaultPreset?.id ?? '';
    liveQueryRef.current = targetQuery;
    setLiveQuery(targetQuery);
    setAdvancedQuery(targetQuery);
    setActivePresetName(targetPresetName);
    setSourcePresetName(undefined);
    setPanelResetKey(k => k + 1);
    setBasicTerm('');
    setIsPanelOpen(false);
    setIsCollapsed(false);
    setIsSaveFormOpen(false);
    setSaveName('');
    setSaveError('');
    const hasRules = targetQuery.filterGroups.some(g => g.rules.length > 0);
    emit(hasRules ? 'advanced' : 'basic', '', targetQuery);
  }, [defaultPreset, emit]);

  const handleAdvancedClear = useCallback(() => {
    // Clear all rules but keep the panel open — the × button handles close.
    const empty = createEmptyAdvancedQuery();
    liveQueryRef.current = empty;
    setLiveQuery(empty);
    setAdvancedQuery(empty);
    setActivePresetName('');
    setSourcePresetName(undefined);
    setPanelResetKey(k => k + 1);
    setIsSaveFormOpen(false);
    setSaveName('');
    setSaveError('');
    emit('basic', '', empty);
  }, [emit]);

  const handlePanelQueryChange = useCallback((query: AdvancedSearchQuery) => {
    liveQueryRef.current = query;
    setLiveQuery(query);
  }, []);

  // ── Preset dropdown handlers ──────────────────────────────────────────────

  const getFilterLabel = (): string => {
    if (!activePresetName || activePresetName === 'Custom') return 'Custom';
    const builtIn = builtInPresets?.find(p => p.id === activePresetName);
    if (builtIn) return builtIn.label;
    const user = userPresets.find(p => p.name === activePresetName);
    if (user) return user.name;
    return 'Custom';
  };

  const applyPreset = useCallback(
    (query: AdvancedSearchQuery, presetName: string) => {
      liveQueryRef.current = query;
      setLiveQuery(query);
      setAdvancedQuery(query);
      setActivePresetName(presetName);
      // Enter Mode B if this is a user preset; clear it for built-ins
      setSourcePresetName(
        userPresets.some(p => p.name === presetName) ? presetName : undefined
      );
      setPanelResetKey(k => k + 1);
      setFilterMenuAnchorEl(null);
      // A built-in preset with an empty query (e.g. "All") resets to basic
      const hasRules = query.filterGroups.some(g => g.rules.length > 0);
      const nextMode: 'basic' | 'advanced' = hasRules ? 'advanced' : 'basic';
      if (!hasRules) {
        setBasicTerm('');
        setIsPanelOpen(false);
        setIsCollapsed(false);
      }
      emit(nextMode, hasRules ? '' : basicTerm, query);
    },
    [emit, basicTerm, userPresets]
  );

  const handleSelectBuiltInPreset = useCallback(
    (preset: FilterPreset) => applyPreset(preset.query, preset.id),
    [applyPreset]
  );

  const handleSelectUserPreset = useCallback(
    (preset: UserPreset) => applyPreset(preset.query, preset.name),
    [applyPreset]
  );

  const handleEditCurrentFilters = useCallback(() => {
    setFilterMenuAnchorEl(null);
    setIsCollapsed(false);
    setIsPanelOpen(true);
  }, []);

  const handleCollapse = useCallback(() => {
    setIsPanelOpen(false);
    setIsCollapsed(true);
    setIsSaveFormOpen(false);
    setSaveName('');
    setSaveError('');
  }, []);

  const handleExpand = useCallback(() => {
    setIsCollapsed(false);
    setIsPanelOpen(true);
  }, []);

  // ── User preset save / overwrite / delete ─────────────────────────────────

  const handleConfirmSave = useCallback(() => {
    const trimmed = saveName.trim();
    if (!trimmed) {
      setSaveError('Name is required.');
      return;
    }
    const existingNames = userPresets.map(p => p.name);
    if (existingNames.includes(trimmed)) {
      setSaveError(`"${trimmed}" already exists.`);
      return;
    }
    // Block saving if the current filters are identical to an existing preset.
    const currentQuery = liveQueryRef.current;
    const dupBuiltIn = builtInPresets?.find(p =>
      queriesAreEqual(p.query, currentQuery)
    );
    if (dupBuiltIn) {
      setSaveError(`Same filters already exist as "${dupBuiltIn.label}".`);
      return;
    }
    const dupUser = userPresets.find(p =>
      queriesAreEqual(p.query, currentQuery)
    );
    if (dupUser) {
      setSaveError(`Same filters already exist as "${dupUser.name}".`);
      return;
    }
    const newPreset: UserPreset = {
      name: trimmed,
      query: liveQueryRef.current,
    };
    setUserPresets(prev => {
      const updated = [...prev, newPreset];
      writeLocalStorage(storageKeyUserPresets, updated);
      return updated;
    });
    setActivePresetName(trimmed);
    setSourcePresetName(trimmed); // stay in Mode B for the newly created preset
    setIsSaveFormOpen(false);
    setSaveName('');
    setSaveError('');
  }, [saveName, userPresets, builtInPresets, storageKeyUserPresets]);

  const handleCancelSave = useCallback(() => {
    setIsSaveFormOpen(false);
    setSaveName('');
    setSaveError('');
  }, []);

  const handleUpdatePreset = useCallback(
    (presetName: string) => {
      const savedQuery = liveQueryRef.current;
      setUserPresets(prev => {
        const updated = prev.map(p =>
          p.name === presetName ? { ...p, query: savedQuery } : p
        );
        writeLocalStorage(storageKeyUserPresets, updated);
        return updated;
      });
      setActivePresetName(presetName);
      // Stay in Mode B — sourcePresetName unchanged. hasChanges will be false
      // since the saved query now matches liveQuery.
    },
    [storageKeyUserPresets]
  );

  const handleDeletePreset = useCallback(
    (presetName: string) => {
      setUserPresets(prev => {
        const updated = prev.filter(p => p.name !== presetName);
        writeLocalStorage(storageKeyUserPresets, updated);
        if (activePresetName === presetName) {
          setActivePresetName('');
          const empty = createEmptyAdvancedQuery();
          setAdvancedQuery(empty);
          liveQueryRef.current = empty;
          emit('basic', '', empty);
        }
        if (sourcePresetName === presetName) {
          setSourcePresetName(undefined);
        }
        return updated;
      });
      setDeletingPresetName(null);
      setFilterMenuAnchorEl(null);
    },
    [storageKeyUserPresets, activePresetName, sourcePresetName, emit]
  );

  const handleConfirmRename = useCallback(
    (oldName: string, newName: string) => {
      const trimmed = newName.trim();
      if (!trimmed) {
        setRenameError('Name is required.');
        return;
      }
      if (trimmed !== oldName && userPresets.some(p => p.name === trimmed)) {
        setRenameError(`"${trimmed}" already exists.`);
        return;
      }
      if (trimmed === oldName) {
        // No change — just close
        setRenamingPresetName(null);
        setRenameInputValue('');
        setRenameError('');
        return;
      }
      setUserPresets(prev => {
        const updated = prev.map(p =>
          p.name === oldName ? { ...p, name: trimmed } : p
        );
        writeLocalStorage(storageKeyUserPresets, updated);
        return updated;
      });
      if (activePresetName === oldName) {
        setActivePresetName(trimmed);
      }
      if (sourcePresetName === oldName) {
        setSourcePresetName(trimmed);
      }
      setRenamingPresetName(null);
      setRenameInputValue('');
      setRenameError('');
      setFilterMenuAnchorEl(null);
    },
    [userPresets, storageKeyUserPresets, activePresetName, sourcePresetName]
  );

  // ── Derived: panel header save-button state ───────────────────────────
  //
  // Mode B (editing a user preset): sourcePresetName is set when a user preset
  // was applied. hasChanges is true when the live query differs from that
  // preset’s saved state.
  //
  // duplicateOf: name of any existing preset that is semantically identical to
  // the current live query (excluding the source preset itself — matching it
  // simply means no changes have been made).
  const sourcePreset = userPresets.find(p => p.name === sourcePresetName);
  const hasChanges =
    sourcePreset !== undefined
      ? !queriesAreEqual(liveQuery, sourcePreset.query)
      : false;

  const duplicateOf: string | undefined = (() => {
    const matchBuiltIn = builtInPresets?.find(p =>
      queriesAreEqual(p.query, liveQuery)
    );
    if (matchBuiltIn) return matchBuiltIn.label;
    const matchUser = userPresets.find(
      p => p.name !== sourcePresetName && queriesAreEqual(p.query, liveQuery)
    );
    return matchUser?.name;
  })();

  // ── Render: collapsed filter summary row ───────────────────────────────────
  const renderCollapsedSummary = () => (
    <div style={styles.collapsedSummary} data-testid="usb-collapsed-summary">
      <span style={styles.collapsedText}>
        {buildSummaryText(fieldConfigs, liveQuery, builtInPresets, userPresets)}
      </span>
      <button
        onClick={handleExpand}
        style={styles.expandButton}
        aria-label="Expand advanced filters"
        title="Expand advanced filters"
        data-testid="usb-expand-button">
        <ChevronRight size={14} />
      </button>
      <button
        onClick={handleRevertAndClose}
        style={styles.panelCloseButton}
        aria-label="Clear filters and return to basic search"
        title="Clear filters"
        data-testid="usb-collapsed-close-button">
        ×
      </button>
    </div>
  );

  // ── Render: save controls in the panel header ───────────────────────────
  const renderPanelFooter = () => {
    if (!hasPresets) return null;

    // ── Name entry form (overlays any mode) ────────────────────────────────
    if (isSaveFormOpen) {
      return (
        <div style={footerStyles.formRow}>
          <input
            type="text"
            placeholder="Preset name…"
            value={saveName}
            onChange={e => {
              setSaveName(e.target.value);
              setSaveError('');
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') handleConfirmSave();
              if (e.key === 'Escape') handleCancelSave();
            }}
            autoFocus
            style={footerStyles.nameInput}
            data-testid="usb-preset-name-input"
          />
          {saveError && (
            <span
              style={footerStyles.errorText}
              data-testid="usb-preset-save-error">
              {saveError}
            </span>
          )}
          <button
            onClick={handleConfirmSave}
            style={footerStyles.primaryButton}
            data-testid="usb-preset-save-confirm">
            Save
          </button>
          <button
            onClick={handleCancelSave}
            style={footerStyles.secondaryButton}>
            Cancel
          </button>
        </div>
      );
    }

    // ── Mode B: editing a named user preset ────────────────────────────────
    if (sourcePreset !== undefined) {
      if (!hasChanges) return null; // no unsaved changes yet

      if (duplicateOf !== undefined) {
        return (
          <span
            style={footerStyles.duplicateText}
            data-testid="usb-preset-duplicate-label">
            Already saved as &ldquo;{duplicateOf}&rdquo;
          </span>
        );
      }

      return (
        <div style={footerStyles.buttonRow}>
          <button
            onClick={() => handleUpdatePreset(sourcePreset.name)}
            style={footerStyles.primaryButton}
            data-testid="usb-preset-update-button"
            title={`Save changes to "${sourcePreset.name}"`}>
            Update &ldquo;{sourcePreset.name}&rdquo;
          </button>
          <button
            onClick={() => {
              setSaveName('');
              setSaveError('');
              setIsSaveFormOpen(true);
            }}
            style={footerStyles.secondaryButton}
            data-testid="usb-preset-save-as-new-button">
            Save as New Preset…
          </button>
        </div>
      );
    }

    // ── Mode A: browsing / creating from scratch ───────────────────────────
    if (duplicateOf !== undefined) {
      return (
        <span
          style={footerStyles.duplicateText}
          data-testid="usb-preset-duplicate-label">
          Already saved as &ldquo;{duplicateOf}&rdquo;
        </span>
      );
    }

    return (
      <div style={footerStyles.buttonRow}>
        <button
          onClick={() => {
            setSaveName('');
            setSaveError('');
            setIsSaveFormOpen(true);
          }}
          style={footerStyles.secondaryButton}
          data-testid="usb-preset-save-button">
          Save as Preset…
        </button>
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={isPanelOpen ? styles.rootRow : styles.root}>
      {/* ── Toolbar row ── */}
      <div style={styles.toolbarRow}>
        {/* Collapsed summary — shown when advanced panel is minimised */}
        {isCollapsed && renderCollapsedSummary()}

        {/* Basic search — hidden while advanced panel is open or collapsed */}
        {!isPanelOpen && !isCollapsed && (
          <div style={styles.basicSearchWrap}>
            <BasicSearch
              searchTerm={basicTerm}
              placeholder={placeholder}
              onSearchTermChange={handleBasicSearchChange}
              onClearSearch={handleBasicSearchClear}
            />
          </div>
        )}

        {/* Filter preset dropdown — only when builtInPresets are supplied */}
        {hasPresets && (
          <>
            <Button
              size="small"
              variant="outlined"
              onClick={e => setFilterMenuAnchorEl(e.currentTarget)}
              endIcon={<ChevronDown size={14} />}
              aria-label="Filter options"
              aria-haspopup="true"
              style={styles.filterButton}>
              Filter: {getFilterLabel()}
            </Button>

            <Menu
              anchorEl={filterMenuAnchorEl}
              open={Boolean(filterMenuAnchorEl)}
              onClose={() => {
                setFilterMenuAnchorEl(null);
                setDeletingPresetName(null);
                setRenamingPresetName(null);
                setRenameInputValue('');
                setRenameError('');
              }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
              {/* Built-in (locked) presets */}
              {builtInPresets?.map(preset => (
                <MenuItem
                  key={preset.id}
                  selected={activePresetName === preset.id}
                  onClick={() => handleSelectBuiltInPreset(preset)}>
                  🔒 {preset.label}
                </MenuItem>
              ))}

              {/* User-saved presets */}
              {userPresets.length > 0 && <Divider />}
              {userPresets.map(preset =>
                renamingPresetName === preset.name ? (
                  <MenuItem
                    key={`${preset.name}-rename`}
                    disableRipple
                    style={styles.deleteConfirmRow}>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.25rem',
                        width: '100%',
                      }}>
                      <input
                        type="text"
                        value={renameInputValue}
                        onChange={e => {
                          setRenameInputValue(e.target.value);
                          setRenameError('');
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter')
                            handleConfirmRename(preset.name, renameInputValue);
                          if (e.key === 'Escape') {
                            setRenamingPresetName(null);
                            setRenameInputValue('');
                            setRenameError('');
                          }
                        }}
                        autoFocus
                        style={{
                          padding: '0.25rem 0.5rem',
                          fontSize: '0.8125rem',
                          border: `1px solid ${GRAY_300}`,
                          borderRadius: '0.25rem',
                          width: '100%',
                          boxSizing: 'border-box',
                        }}
                        data-testid={`usb-rename-input-${preset.name}`}
                      />
                      {renameError && (
                        <span
                          style={{ fontSize: '0.75rem', color: COLOR_DANGER }}>
                          {renameError}
                        </span>
                      )}
                      <div style={styles.deleteConfirmButtons}>
                        <button
                          data-testid={`usb-confirm-rename-preset-${preset.name}`}
                          onClick={e => {
                            e.stopPropagation();
                            handleConfirmRename(preset.name, renameInputValue);
                          }}
                          style={styles.deleteConfirmYes}>
                          Save
                        </button>
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            setRenamingPresetName(null);
                            setRenameInputValue('');
                            setRenameError('');
                          }}
                          style={styles.deleteConfirmNo}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  </MenuItem>
                ) : deletingPresetName === preset.name ? (
                  <MenuItem
                    key={`${preset.name}-confirm`}
                    disableRipple
                    style={styles.deleteConfirmRow}>
                    <span style={styles.deleteConfirmLabel}>
                      Delete &ldquo;{preset.name}&rdquo;?
                    </span>
                    <div style={styles.deleteConfirmButtons}>
                      <button
                        data-testid={`usb-confirm-delete-preset-${preset.name}`}
                        onClick={e => {
                          e.stopPropagation();
                          handleDeletePreset(preset.name);
                        }}
                        style={styles.deleteConfirmYes}>
                        Delete
                      </button>
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          setDeletingPresetName(null);
                        }}
                        style={styles.deleteConfirmNo}>
                        Cancel
                      </button>
                    </div>
                  </MenuItem>
                ) : (
                  <MenuItem
                    key={preset.name}
                    selected={activePresetName === preset.name}
                    onClick={() => handleSelectUserPreset(preset)}
                    style={styles.userPresetRow}>
                    <span>{preset.name}</span>
                    <span style={styles.userPresetActions}>
                      <span
                        role="button"
                        tabIndex={-1}
                        aria-label={`Edit filter preset "${preset.name}"`}
                        data-testid={`usb-edit-preset-${preset.name}`}
                        onClick={e => {
                          e.stopPropagation();
                          setRenamingPresetName(preset.name);
                          setRenameInputValue(preset.name);
                          setRenameError('');
                          setDeletingPresetName(null);
                        }}
                        style={styles.presetEditIcon}>
                        <Pencil size={13} />
                      </span>
                      <span
                        role="button"
                        tabIndex={-1}
                        aria-label={`Delete filter preset "${preset.name}"`}
                        data-testid={`usb-delete-preset-${preset.name}`}
                        onClick={e => {
                          e.stopPropagation();
                          setDeletingPresetName(preset.name);
                        }}
                        style={styles.presetDeleteIcon}>
                        <Trash2 size={13} />
                      </span>
                    </span>
                  </MenuItem>
                )
              )}

              <Divider />
              <MenuItem onClick={handleEditCurrentFilters}>
                <span style={styles.editFiltersIcon}>
                  <SlidersHorizontal
                    size={14}
                    style={{ marginRight: '0.5rem' }}
                  />
                </span>
                Edit current filters…
              </MenuItem>
            </Menu>
          </>
        )}

        {/* Caller-supplied toolbar actions (DensityToggle, Add button, etc.) */}
        {actions}
      </div>

      {/* ── Advanced filter panel ── */}
      {isPanelOpen && (
        <div style={styles.panelSide} data-testid="usb-advanced-panel">
          {/* Panel header */}
          <div style={styles.panelHeader}>
            <span style={styles.panelTitle}>Advanced Filters</span>
            <div style={styles.panelHeaderRight}>
              {renderPanelFooter()}
              <button
                onClick={handleCollapse}
                aria-label="Collapse advanced filters"
                title="Collapse filters"
                style={styles.collapseButton}
                data-testid="usb-collapse-button">
                <ChevronDown size={14} />
              </button>
              <button
                onClick={handleRevertAndClose}
                aria-label="Clear filters and close"
                title="Clear and close"
                style={styles.panelCloseButton}
                data-testid="usb-close-button">
                ×
              </button>
            </div>
          </div>

          <UnifiedAdvancedSearch
            key={panelResetKey}
            fieldConfigs={fieldConfigs}
            query={liveQuery}
            onSearch={handleAdvancedSearch}
            onClear={handleAdvancedClear}
            onQueryChange={handlePanelQueryChange}
          />
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles: Record<string, React.CSSProperties> = {
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  rootRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: '0.75rem',
  },
  toolbarRow: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },
  basicSearchWrap: {
    flex: 1,
    minWidth: 200,
  },
  filterButton: {
    whiteSpace: 'nowrap',
    textTransform: 'none',
    fontSize: '0.8125rem',
  },
  panel: {
    marginTop: '0.5rem',
    border: `1px solid ${GRAY_300}`,
    borderRadius: '0.5rem',
    padding: '0.75rem 1rem',
    backgroundColor: GRAY_50,
  },
  panelSide: {
    order: -1,
    flex: 1,
    minWidth: 0,
    border: `1px solid ${GRAY_300}`,
    borderRadius: '0.5rem',
    padding: '0.75rem 1rem',
    backgroundColor: GRAY_50,
  },
  panelHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.5rem',
    minHeight: '2.25rem',
  },
  panelHeaderRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  panelTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: GRAY_700,
  },
  panelCloseButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: GRAY_500,
    fontSize: '1.125rem',
    lineHeight: 1,
    padding: '0 0.25rem',
  },
  deleteConfirmRow: {
    minWidth: 240,
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  deleteConfirmLabel: {
    fontSize: '0.8125rem',
    color: GRAY_700,
    marginBottom: '0.25rem',
  },
  deleteConfirmButtons: {
    display: 'flex',
    gap: '0.5rem',
  },
  deleteConfirmYes: {
    padding: '0.25rem 0.75rem',
    fontSize: '0.75rem',
    border: `1px solid ${COLOR_DANGER}`,
    borderRadius: '0.25rem',
    backgroundColor: COLOR_DANGER,
    color: 'white',
    cursor: 'pointer',
  },
  deleteConfirmNo: {
    padding: '0.25rem 0.75rem',
    fontSize: '0.75rem',
    border: `1px solid ${GRAY_300}`,
    borderRadius: '0.25rem',
    backgroundColor: 'white',
    cursor: 'pointer',
  },
  userPresetRow: {
    display: 'flex',
    justifyContent: 'space-between',
    minWidth: 240,
  },
  userPresetActions: {
    display: 'flex',
    gap: '0.5rem',
    marginLeft: '1.5rem',
  },
  presetEditIcon: {
    cursor: 'pointer',
    color: '#d97706',
    display: 'flex',
    alignItems: 'center',
  },
  presetDeleteIcon: {
    cursor: 'pointer',
    color: '#dc2626',
    display: 'flex',
    alignItems: 'center',
  },
  editFiltersIcon: {
    color: GRAY_500,
    display: 'flex',
    alignItems: 'center',
  },
  collapseButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: GRAY_500,
    display: 'flex',
    alignItems: 'center',
    padding: '0 0.25rem',
  },
  collapsedSummary: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
    padding: '0.375rem 0.75rem',
    backgroundColor: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: '0.375rem',
    overflow: 'hidden',
  },
  collapsedText: {
    flex: 1,
    minWidth: 0,
    fontSize: '0.8125rem',
    color: COLOR_INFO_TEXT,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  expandButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: COLOR_INFO,
    display: 'flex',
    alignItems: 'center',
    padding: '0 0.125rem',
    flexShrink: 0,
  },
};

const footerStyles: Record<string, React.CSSProperties> = {
  buttonRow: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
  },
  formRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  nameInput: {
    width: 140,
    padding: '0.25rem 0.5rem',
    fontSize: '0.8125rem',
    border: `1px solid ${GRAY_300}`,
    borderRadius: '0.25rem',
    outline: 'none',
  },
  errorText: {
    fontSize: '0.75rem',
    color: '#dc2626',
  },
  primaryButton: {
    padding: '0.375rem 0.875rem',
    fontSize: '0.8125rem',
    border: '1px solid #2563eb',
    borderRadius: '0.25rem',
    backgroundColor: '#2563eb',
    color: 'white',
    cursor: 'pointer',
  },
  secondaryButton: {
    padding: '0.375rem 0.875rem',
    fontSize: '0.8125rem',
    border: `1px solid ${GRAY_300}`,
    borderRadius: '0.25rem',
    backgroundColor: 'white',
    color: GRAY_700,
    cursor: 'pointer',
  },
  duplicateText: {
    fontSize: '0.75rem',
    color: GRAY_500,
    fontStyle: 'italic',
    whiteSpace: 'nowrap',
  } as React.CSSProperties,
};
